import { removeLoadingState, setVisibility } from "../helpers";

/**
 * Initializes BSN check/confirm form interactions.
 * 
 * Expects the following structure:
 *   - <div data-bsn-show="readonly|check|confirm">  (main container)
 *   - <sds-card>...</sds-card>  (3 cards: readonly, check, confirm)
 *   - <sds-form data-bsn-form="check">  (check form)
 *   - <sds-form data-bsn-form="confirm">  (confirm form)
 *   - <button data-bsn-back="true">  (back button in confirm form)
 */
export function initBsnEditPartial() {
    const container = document.querySelector('[data-bsn-show]');
    if (!container) return;

    const checkForm = document.querySelector('[data-bsn-form="check"]');
    const confirmForm = document.querySelector('[data-bsn-form="confirm"]');
    
    if (!checkForm || !confirmForm) return;

    // Setup event listeners
    checkForm.addEventListener("sds:form:beforeSubmit", handleCheckFormSubmit);
    confirmForm.addEventListener("sds:form:beforeSubmit", handleConfirmFormSubmit);

    const backButton = confirmForm.querySelector('[data-bsn-back="true"]');
    if (backButton) {
        backButton.addEventListener("click", handleBackClick);
    }

    /**
     * Updates visibility of all BSN cards based on current state
     */
    function setDisplayState(state) {
        const readonlyCard = container.querySelector('[data-bsn-card="readonly"]');
        const checkCard = container.querySelector('[data-bsn-card="check"]');
        const confirmCard = container.querySelector('[data-bsn-card="confirm"]');

        setVisibility(readonlyCard, state === "readonly");
        setVisibility(checkCard, state === "check");
        setVisibility(confirmCard, state === "confirm");

        container.dataset.bsnShow = state;
    }

    /**
     * Handle check form submission
     */
    function handleCheckFormSubmit(event) {
        event.preventDefault();

        const url = checkForm.getAttribute("action");
        const formData = new URLSearchParams(new FormData(checkForm));
        const submitButton = checkForm.querySelector("button[type='submit']");

        // Disable buttons and show loading state
        const buttons = checkForm.querySelectorAll("button");
        if (submitButton) {
            removeLoadingState(submitButton); // Clear any prior loading state
            submitButton.classList.add("loading");
        }
        buttons.forEach(btn => btn.setAttribute("disabled", "disabled"));

        // Clear previous errors
        clearErrors(".documentbsncheckerrors");

        fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: formData
        })
            .then(response => response.json())
            .then(responseJson => {
                // Print errors if any
                printErrors(".documentbsncheckerrors", responseJson.errors);

                if (responseJson.errors && responseJson.errors.length === 0) {
                    // Success: update BSN display values and move to confirm state
                    updateBsnValues(responseJson);
                    setDisplayState("confirm");
                } else {
                    // Errors: re-enable buttons
                    buttons.forEach(btn => btn.removeAttribute("disabled"));
                }
            })
            .catch(() => {
                buttons.forEach(btn => btn.removeAttribute("disabled"));
            })
            .finally(() => {
                if (submitButton) {
                    submitButton.classList.remove("loading");
                }
            });
    }

    /**
     * Handle confirm form submission
     */
    function handleConfirmFormSubmit(event) {
        event.preventDefault();

        const url = confirmForm.getAttribute("action");
        const formData = new URLSearchParams(new FormData(confirmForm));
        const submitButton = confirmForm.querySelector("button[type='submit']");

        // Disable buttons and show loading state
        const buttons = confirmForm.querySelectorAll("button");
        if (submitButton) {
            removeLoadingState(submitButton);
            submitButton.classList.add("loading");
        }
        buttons.forEach(btn => btn.setAttribute("disabled", "disabled"));

        // Clear previous errors
        clearErrors(".documentbsncheckerrors");

        fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: formData
        })
            .then(response => response.json())
            .then(responseJson => {
                // Print errors if any
                printErrors(".documentbsncheckerrors", responseJson.errors);

                if (responseJson.errors && responseJson.errors.length === 0) {
                    // Success: move to readonly state
                    setDisplayState("readonly");
                } else {
                    // Errors: re-enable buttons
                    buttons.forEach(btn => btn.removeAttribute("disabled"));
                }
            })
            .catch(() => {
                buttons.forEach(btn => btn.removeAttribute("disabled"));
            })
            .finally(() => {
                if (submitButton) {
                    submitButton.classList.remove("loading");
                }
            });
    }

    /**
     * Handle back button click: return to check state
     */
    function handleBackClick(event) {
        event.preventDefault();
        clearErrors(".documentbsncheckerrors");
        setDisplayState("check");
    }

    /**
     * Update BSN display values in the readonly card from API response
     * Updates the sds-definition-list items by setting their value attribute
     */
    function updateBsnValues(responseJson) {
        const readonlyCard = container.querySelector('[data-bsn-card="readonly"]');
        if (!readonlyCard) return;

        if (responseJson.bsnFirstApplicant) {
            const firstItem = readonlyCard.querySelector('[data-bsn-applicant="first"]');
            if (firstItem) firstItem.setAttribute("value", responseJson.bsnFirstApplicant.trim());
        }

        if (responseJson.bsnSecondApplicant) {
            const secondItem = readonlyCard.querySelector('[data-bsn-applicant="second"]');
            if (secondItem) secondItem.setAttribute("value", responseJson.bsnSecondApplicant.trim());
        }
    }

    /**
     * Render error messages in the error container
     */
    function printErrors(errorContainerSelector, errors) {
        const errorContainer = document.querySelector(errorContainerSelector);
        if (!errorContainer) return;

        errorContainer.innerHTML = "";

        if (errors && errors.length > 0) {
            errorContainer.classList.add("validation-summary-errors");
            errors.forEach(error => {
                const span = document.createElement("span");
                span.className = "field-validation-error";
                span.textContent = error;
                errorContainer.appendChild(span);
            });
        }
    }

    /**
     * Clear error messages from container
     */
    function clearErrors(errorContainerSelector) {
        const errorContainer = document.querySelector(errorContainerSelector);
        if (errorContainer) {
            errorContainer.innerHTML = "";
            errorContainer.classList.remove("validation-summary-errors");
        }
    }
}